import {getHighlightedEvent, isEventHighlighted} from '@github-ui/timeline-items/HighlightedEvent'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {fetchQuery, useRelayEnvironment} from 'react-relay'
import {
  createOperationDescriptor,
  type Disposable,
  getRequest,
  type GraphQLTaggedNode,
  type OperationDescriptor,
  type Variables,
} from 'relay-runtime'

import type {useTimelineHighlightItems$data} from './documents/__generated__/useTimelineHighlightItems.graphql'
import type {TimelineItem} from './IssueTimeline'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'
import {
  assembleTimelineItems,
  countItemsRemaining,
  isHighlightReady,
  subtractItems,
  type TimelineCounters,
  type TimelineSlot,
} from './timeline-assembly'
import {useTimelineHighlights} from './use-timeline-highlight'

// ─── Types ───────────────────────────────────────────────────────────────────

/**
 * The normalised edge shape shared by all timeline connections (front, back,
 * conversation, activity). Every caller must reduce its Relay fragment data
 * to this shape before passing it to the core hook.
 */
export type TimelineEdgeData = {
  totalCount: number
  edges:
    | ReadonlyArray<
        | {
            readonly node:
              | {
                  readonly __id: string
                }
              | null
              | undefined
          }
        | null
        | undefined
      >
    | null
    | undefined
}

/**
 * Configuration for the back-items fetch query.
 *
 * All back-item queries follow the same pattern:
 * `{ node(id) { ... on Issue { <connectionKey>: timelineItems(...) { ... } } } }`
 *
 * Use `createBackFetcherConfig` to build one from a query + connection key.
 */
export type BackFetcherConfig = {
  query: GraphQLTaggedNode
  makeVariables: (issueId: string, count: number, cursor: string | null) => Variables
  extractItems: (response: unknown) => {
    items: IssueTimelineItemFragment[]
    startCursor: string | null
    hasPreviousPage: boolean
  } | null
}

type BackConnectionData = TimelineEdgeData & {pageInfo: {startCursor: string | null; hasPreviousPage?: boolean}}

/**
 * Factory for creating a `BackFetcherConfig` from a query and its connection
 * property name on the Issue node (e.g. `'backTimelineItems'`).
 *
 * All back-item queries share the same variables shape and response structure,
 * so this eliminates the boilerplate that would otherwise be duplicated across
 * classic, conversation, and activity hooks.
 */
export function createBackFetcherConfig(
  query: GraphQLTaggedNode,
  connectionKey: string,
  additionalVariables: Variables = {},
): BackFetcherConfig {
  return {
    query,
    makeVariables: (issueId, count, cursor) => ({id: issueId, count, cursor, ...additionalVariables}),
    extractItems: resp => {
      const response = resp as {node?: Record<string, unknown> | null}
      const issueNode = response.node
      if (issueNode && connectionKey in issueNode) {
        const backData = issueNode[connectionKey] as BackConnectionData | null | undefined
        if (backData) {
          return {
            items: mapAndFilterTimelineItems(backData),
            startCursor: backData.pageInfo.startCursor ?? null,
            hasPreviousPage: backData.pageInfo.hasPreviousPage ?? true,
          }
        }
      }
      return null
    },
  }
}

export type UseTimelineItemsCoreProps = {
  issueId: string
  /** Raw edge data from the front pagination fragment. Mapped to items inside the core. */
  frontData: TimelineEdgeData | undefined
  /** Raw edge data from the back pagination fragment. Mapped to items inside the core. */
  backData: TimelineEdgeData | undefined
  /** Forward pagination function from `usePaginationFragment`. */
  loadMoreFrontItems: (count: number) => void
  /** Fetcher configuration for loading back items. */
  backFetcherConfig: BackFetcherConfig
  /** Optional GraphQL item-type filter shared by focused timeline reads. */
  timelineItemTypes?: ReadonlyArray<'ISSUE_COMMENT'>
  /** Optional: highlighted event text (e.g. "issuecomment-456"). */
  highlightedEvent?: string | undefined
  /** Optional: page number for bot pagination. */
  page?: number
  /** Optional: Maximum number of back items to auto-load on initial render. Defaults to 15. */
  initialBackPageSize?: number
}

export type UseTimelineItemsCoreResult = {
  timelineItems: TimelineItem[] | null
  totalItemCount: number
  highlightedReady: boolean
  /**
   * The opening reads are still in flight, so an empty list is not yet an empty timeline. Absent on
   * the Relay path, which renders only once its fragments are in the store.
   */
  isLoading?: boolean
  /**
   * The cache key of the newest section -- the one at the bottom of the timeline, holding its end.
   * An appended row can only land there, so live updates invalidate this key rather than the whole
   * timeline prefix, which would refetch every mounted section to pick up a row only this one can
   * hold.
   *
   * Absent on the Relay path, which does not read through TanStack, and while the newest section
   * has no anchor -- until it does, its key names a window the appended row falls outside of.
   */
  newestSectionQueryKey?: readonly unknown[]
  onCanonicalTimelineUpdated?: () => void | Promise<void>
}

// ─── Core Hook ───────────────────────────────────────────────────────────────

/**
 * Generic hook responsible for combining front, highlight, and back items
 * into a single paginated timeline list. Handles:
 *
 * - Auto-loading initial back items
 * - Highlighted event fetching & stitching
 * - Load-more markers between sections
 * - Deduplication & rollups
 *
 * Consumers provide raw edge data from their Relay fragments and a fetcher
 * config for the back-items query. This allows the same core logic to be used
 * by the timeline with different fragments.
 */
export function useTimelineItemsCore({
  issueId,
  frontData,
  backData,
  loadMoreFrontItems,
  backFetcherConfig,
  timelineItemTypes,
  highlightedEvent,
  page,
  initialBackPageSize = 15,
}: UseTimelineItemsCoreProps): UseTimelineItemsCoreResult {
  // This object is responsible for keeping track of the positioning and number of items in each section of the timeline.
  // That state depends on responses from the server but also user actions, so we aggregate the data here.
  const [timelineCounters, setTimelineCounters] = useState<TimelineCounters>({
    frontItems: 0,
    highlightItems: 0,
    highlightStartPosition: 0,
    backItems: 0,
  })

  // This state is used to determine if the highlighted event has been fetched to prevent reloading if
  // a user clicks a comment link more than once in succession.
  // We reset this state when the highlighted event changes.
  const [fetched, setFetched] = useState(false)
  // This state is used to determine if the user has clicked on a different highlighted event
  const [lastFetchedHighlight, setLastFetchedHighlight] = useState<string | null>(null)
  const [lastTotalBeforeFocus, setLastTotalBeforeFocus] = useState<number | null>(null)
  const [shouldLoadBackItems, setShouldLoadBackItems] = useState(
    () =>
      !page &&
      mapAndFilterTimelineItems(backData).length === 0 &&
      mapAndFilterTimelineItems(frontData).length < Math.max(frontData?.totalCount ?? 0, 0),
  )
  const [loadingInitialBackItems, setLoadingInitialBackItems] = useState(shouldLoadBackItems)

  // State for managing custom back items pagination
  const [backItems, setBackItems] = useState<IssueTimelineItemFragment[]>([])
  // The operations behind those rows, newest page first, so they can be re-read from the store when
  // the records they render change. See the subscription below.
  const [backPages, setBackPages] = useState<OperationDescriptor[]>([])
  const [backItemsCursor, setBackItemsCursor] = useState<string | null>(null)
  const [isLoadingBackItems, setIsLoadingBackItems] = useState(false)

  const environment = useRelayEnvironment()

  // Retain the operation descriptor for each back-items page so Relay's GC can't collect the
  // records it brought in while those pages are still rendered -- which is also what keeps them
  // readable by the store subscription below. Disposed on unmount.
  const backPageDisposablesRef = useRef<Disposable[]>([])
  useEffect(() => {
    return () => {
      for (const disposable of backPageDisposablesRef.current) {
        disposable.dispose()
      }
      backPageDisposablesRef.current = []
    }
  }, [])

  /**
   * Keeps the loaded back pages reading from the Relay store instead of the snapshot each fetch
   * returned.
   *
   * The rows below the front page are fetched imperatively, so nothing subscribed them to the
   * records they render. A record-level change -- a comment deleted (`@deleteRecord` on the
   * subscription's `deletedCommentId`), its body edited, minimised -- left them showing the values
   * they were fetched with until the page was reloaded. `IssueComment` used to re-read each record
   * through `useFragment`, which hid this; it no longer does.
   */
  useEffect(() => {
    if (backPages.length === 0) return
    const rereadFromStore = () =>
      setBackItems(
        backPages.flatMap(
          operation => backFetcherConfig.extractItems(environment.lookup(operation.fragment).data)?.items ?? [],
        ),
      )
    const subscriptions = backPages.map(operation =>
      environment.subscribe(environment.lookup(operation.fragment), rereadFromStore),
    )
    return () => {
      for (const subscription of subscriptions) subscription.dispose()
    }
  }, [backPages, environment, backFetcherConfig])

  // Custom loadMoreBackItems function using fetchQuery
  const loadMoreBackItems = useCallback(
    (count: number, options?: {onComplete?: () => void}) => {
      if (isLoadingBackItems) return

      setIsLoadingBackItems(true)

      const variables = backFetcherConfig.makeVariables(issueId, count, backItemsCursor)
      const operation = createOperationDescriptor(getRequest(backFetcherConfig.query), variables)
      const disposable = environment.retain(operation)
      backPageDisposablesRef.current.push(disposable)

      fetchQuery(environment, backFetcherConfig.query, variables, {fetchPolicy: 'store-or-network'}).subscribe({
        next: (response: unknown) => {
          const extracted = backFetcherConfig.extractItems(response)
          if (extracted) {
            setBackItems(prev => [...extracted.items, ...prev])
            setBackPages(prev => [operation, ...prev])
            setBackItemsCursor(extracted.startCursor)
          }
        },
        complete: () => {
          setIsLoadingBackItems(false)
          options?.onComplete?.()
        },
        error: () => {
          // The fetch failed, so the operation has no useful records to keep
          // alive. Drop the retain to avoid leaking it across the component's
          // lifetime.
          disposable.dispose()
          backPageDisposablesRef.current = backPageDisposablesRef.current.filter(d => d !== disposable)
          setIsLoadingBackItems(false)
          options?.onComplete?.()
        },
      })
    },
    [backItemsCursor, environment, isLoadingBackItems, issueId, backFetcherConfig],
  )

  const totalItemCount = Math.max(frontData?.totalCount ?? 0, 0)

  const frontItems = useMemo(() => mapAndFilterTimelineItems(frontData), [frontData])
  const emptyBackItems = useMemo(() => mapAndFilterTimelineItems(backData), [backData])

  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])
  // This means that the highlighted item is already in the front or back items so no need to go back to the server
  const highlightIsPreloaded = useMemo(
    () =>
      highlight
        ? [...frontItems, ...backItems, ...emptyBackItems].some(event => isEventHighlighted(event, highlight))
        : false,
    [backItems, frontItems, highlight, emptyBackItems],
  )
  const hasFrontTimelineData = !!frontData

  useEffect(() => {
    if (!hasFrontTimelineData) {
      throw new Error(
        `Missing front timeline items for issue ${issueId}.  After refetching, the frontTimelineItems should be defined.`,
      )
    }
  }, [hasFrontTimelineData, issueId])

  useEffect(() => {
    if (backItems.length + emptyBackItems.length === 0 && !page) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShouldLoadBackItems(true)
    }
  }, [backItems.length, emptyBackItems.length, page])

  useEffect(() => {
    if (shouldLoadBackItems) {
      const backItemsToFetch = Math.min(initialBackPageSize, totalItemCount - frontItems.length)
      if (backItemsToFetch > 0) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setLoadingInitialBackItems(true)
        loadMoreBackItems(backItemsToFetch, {
          onComplete: () => {
            setShouldLoadBackItems(false)
            setLoadingInitialBackItems(false)
          },
        })
      }
    }
  }, [loadMoreBackItems, frontItems.length, shouldLoadBackItems, totalItemCount, initialBackPageSize])

  const {
    data: highlightData,
    loadPrevious: loadBeforeHighlight,
    totalBeforeFocus,
    loadNext: loadAfterHighlight,
  } = useTimelineHighlights(issueId, highlight, !fetched && highlight && !highlightIsPreloaded, timelineItemTypes)

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setLastTotalBeforeFocus(totalBeforeFocus)
  }, [totalBeforeFocus])

  const loadedHighlightItems = useMemo(() => {
    if (highlightData) {
      return mapAndFilterTimelineItems(highlightData)
    }
    return []
  }, [highlightData])

  useEffect(() => {
    if (highlightData) {
      if (highlightedEvent) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setFetched(!(lastFetchedHighlight && highlightedEvent !== lastFetchedHighlight))
        setLastFetchedHighlight(highlightedEvent)
      }
    }
  }, [highlight, highlightData, highlightedEvent, lastFetchedHighlight])

  // If a highlighted event (or its neighbor) exists in the front or back items, we need to remove it from the loaded highlight items
  const uniqueLoadedHighlightItems = useMemo(
    () =>
      loadedHighlightItems.length > 0
        ? subtractItems(loadedHighlightItems, [...frontItems, ...backItems, ...emptyBackItems])
        : [],
    [frontItems, backItems, emptyBackItems, loadedHighlightItems],
  )

  const hasLazyLoadedHighlights = uniqueLoadedHighlightItems.length > 0

  const {front: itemsRemainingFront, back: itemsRemainingBack} = countItemsRemaining({
    hasLazyLoadedHighlights,
    timelineCounters,
    totalItemCount,
    frontItemCount: frontItems.length,
    backItemCount: backItems.length + emptyBackItems.length,
  })

  const slots: TimelineSlot[] | null = useMemo(
    () =>
      assembleTimelineItems({
        frontItems,
        backItems,
        emptyBackItems,
        uniqueLoadedHighlightItems,
        totalItemCount,
        itemsRemainingFront,
        itemsRemainingBack,
        hasLazyLoadedHighlights,
        loadingInitialBackItems,
        page,
      }),
    [
      frontItems,
      backItems,
      emptyBackItems,
      uniqueLoadedHighlightItems,
      totalItemCount,
      itemsRemainingFront,
      itemsRemainingBack,
      hasLazyLoadedHighlights,
      loadingInitialBackItems,
      page,
    ],
  )

  const timelineItems: TimelineItem[] | null = useMemo(
    () =>
      slots?.map(slot => {
        if (slot.type !== 'load-marker') return slot

        const {type: _marker, ...rest} = slot
        return slot.position === 'top'
          ? {
              ...rest,
              type: 'load',
              loadFromTop: loadMoreFrontItems,
              loadFromBottom: (count: number, options?: {onComplete?: () => void}) => {
                if (hasLazyLoadedHighlights) {
                  loadBeforeHighlight(count, options)
                } else {
                  loadMoreBackItems(count, options)
                }
              },
            }
          : {...rest, type: 'load', loadFromTop: loadAfterHighlight, loadFromBottom: loadMoreBackItems}
      }) ?? null,
    [slots, hasLazyLoadedHighlights, loadMoreFrontItems, loadMoreBackItems, loadBeforeHighlight, loadAfterHighlight],
  )

  useEffect(() => {
    if (
      timelineCounters.frontItems !== frontItems.length ||
      timelineCounters.backItems !== backItems.length + emptyBackItems.length ||
      timelineCounters.highlightItems !== uniqueLoadedHighlightItems.length ||
      lastTotalBeforeFocus !== totalBeforeFocus
    ) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setTimelineCounters(current => {
        return {
          frontItems: frontItems.length,
          backItems: backItems.length + emptyBackItems.length,
          highlightItems: uniqueLoadedHighlightItems.length,
          // This is required since in pagination requests the server will always return 'totalBeforeFocus: 0' even if we do have a highlighted item
          highlightStartPosition: totalBeforeFocus > 0 ? totalBeforeFocus : current.highlightStartPosition,
        }
      })
    }
  }, [
    frontItems,
    backItems,
    emptyBackItems,
    uniqueLoadedHighlightItems,
    totalBeforeFocus,
    timelineCounters.frontItems,
    timelineCounters.backItems,
    timelineCounters.highlightItems,
    lastTotalBeforeFocus,
  ])

  const highlightedReady = isHighlightReady(
    totalItemCount,
    backItems.length + emptyBackItems.length,
    timelineCounters,
    highlightIsPreloaded,
  )

  return {timelineItems, totalItemCount, highlightedReady}
}

/**
 * Maps raw edge data from any timeline connection to typed timeline items.
 * Works with front, back, conversation, activity, and highlight connections.
 */
function mapAndFilterTimelineItems(
  data: TimelineEdgeData | useTimelineHighlightItems$data['timelineItems'] | undefined,
): IssueTimelineItemFragment[] {
  if (!data) return []

  return (data.edges || []).reduce((items: IssueTimelineItemFragment[], edge) => {
    // RELAY-CLEANUP(timeline pagination): the spreading documents are still Relay-owned, so their
    // node types carry open-enum members and omit the `__is*` discriminators. Selections match.
    if (edge?.node?.__id) items.push(edge.node as IssueTimelineItemFragment)
    return items
  }, [])
}
